import { useState, type ReactNode } from 'react'
import { Radio, RadioButton } from '@systemeio/components'
import { useTranslation } from 'next-i18next'
import { useRouter } from 'next/router'
import { translateServerSideProps } from 'shared/utils/translate-server-side-props'
import ResubscribedCard from 'modules/unsubscribe-page/components/resubscribed-card'
import UnsubscribeCard from 'modules/unsubscribe-page/components/unsubscribed-card'
import { UnsubscribeTypeEnum } from 'modules/unsubscribe-page/enum/unsubscribe-type-enum'
import { useDisplayUnsubscribeFromCampaign } from 'modules/unsubscribe-page/hooks/use-display-unsubscribe-from-campaign'
import { useResubscribe } from 'modules/unsubscribe-page/hooks/use-resubscribe'
import { useUnsubscribe } from 'modules/unsubscribe-page/hooks/use-unsubscribe'
import { getUnsubscribePageLayout } from 'modules/unsubscribe-page/layouts/get-unsubscribe-page-layout'

function Unsubscribe(): ReactNode {
  const { t } = useTranslation()
  const { query } = useRouter()

  const path = Array.isArray(query.path) ? query.path : []
  const slug = path[2] ?? ''
  const messageId = path[3] ?? ''

  const [unsubscribeType, setUnsubscribeType] = useState<UnsubscribeTypeEnum>(
    UnsubscribeTypeEnum.unsubscribe_all_marketing_emails,
  )
  const [isSuccess, setIsSuccess] = useState(false)

  const { data } = useDisplayUnsubscribeFromCampaign(messageId)

  const {
    unsubscribeFromAllMarketingEmails,
    unsubscribeFromCampaign,
    isFetching: isFetchingUnsubscribe,
  } = useUnsubscribe()

  const {
    resubscribeToMarketingEmails,
    resubscribeToMarketingEmailsCampaign,
    isFetching: isFetchingResubscribe,
  } = useResubscribe()

  const campaignId = data?.campaign?.id ? String(data.campaign.id) : null
  const isCampaignUnsubscribe = unsubscribeType === UnsubscribeTypeEnum.unsubscribe_campaign

  const options: { value: UnsubscribeTypeEnum; label: string }[] = [
    ...(data?.displayUnsubscribeFromCampaignLink
      ? [
          {
            value: UnsubscribeTypeEnum.unsubscribe_campaign,
            label: t('unsubscribe_page.unsubscribe.from_campaign'),
          },
        ]
      : []),
    {
      value: UnsubscribeTypeEnum.unsubscribe_all_marketing_emails,
      label: t('unsubscribe_page.unsubscribe.from_all_marketing_emails'),
    },
  ]

  const successSubtitle = isCampaignUnsubscribe
    ? t('mailing.unsubscribed_page.unsubscribed.campaign.text')
    : t('mailing.unsubscribed_page.unsubscribed.marketing_emails.text')

  const handleConfirmResubscribed = async (): Promise<void> => {
    if (!data) return

    if (isCampaignUnsubscribe && campaignId) {
      await resubscribeToMarketingEmailsCampaign(slug, messageId, campaignId)
      return
    }

    await resubscribeToMarketingEmails(slug, messageId)
  }

  const handleConfirmUnsubscribe = async (): Promise<void> => {
    if (!data) return

    if (isCampaignUnsubscribe && campaignId) {
      await unsubscribeFromCampaign(slug, messageId, campaignId)
    } else {
      await unsubscribeFromAllMarketingEmails(slug, messageId)
    }

    setIsSuccess(true)
  }

  return (
    <div className="flex flex-col items-center justify-center">
      {isSuccess ? (
        <ResubscribedCard
          title={t('mailing.unsubscribe_page.unsubscribed_success.title')}
          subTitle={successSubtitle}
          isFetching={isFetchingResubscribe}
          onConfirmResubscribed={handleConfirmResubscribed}
        />
      ) : data ? (
        <UnsubscribeCard
          isSuccess
          isFetching={isFetchingUnsubscribe}
          onConfirmUnsubscribe={handleConfirmUnsubscribe}
        >
          <Radio.Group
            className="mb-4 w-full items-start"
            value={unsubscribeType}
            onValueChange={setUnsubscribeType}
            disabled={isFetchingUnsubscribe}
          >
            {options.map(o => (
              <RadioButton key={String(o.value)} value={o.value} label={o.label} />
            ))}
          </Radio.Group>
        </UnsubscribeCard>
      ) : (
        <UnsubscribeSkeleton />
      )}
    </div>
  )
}

function UnsubscribeSkeleton(): ReactNode {
  return (
    <>
      <div className="mb-3 size-37.25 rounded-full bg-bg-tertiary" />
      <div className="w-full">
        <div className="mb-8 h-13.75 w-full rounded-xs bg-bg-tertiary" />
        <div className="mb-4 flex items-center gap-2">
          <div className="size-6 rounded-full bg-bg-tertiary" />
          <div className="h-6 w-3/4 rounded-xs bg-bg-tertiary" />
        </div>
        <div className="mb-8 flex items-center gap-2">
          <div className="size-6 rounded-full bg-bg-tertiary" />
          <div className="h-6 w-3/4 rounded-xs bg-bg-tertiary" />
        </div>
        <div className="h-8 w-full rounded-xs bg-bg-tertiary" />
      </div>
    </>
  )
}

export const getServerSideProps = translateServerSideProps

Unsubscribe.getLayout = getUnsubscribePageLayout

export default Unsubscribe
